import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  createAutorivFastener,
  deleteAutorivFastener,
  getAutorivFastener,
  listAutorivFasteners,
  listAutorivFastenerGroups,
  updateAutorivFastener,
  type AutorivFastener,
  type CreateAutorivFastenerBody,
  type ListAutorivFastenersParams,
  type PaginatedAutorivFasteners,
  type UpdateAutorivFastenerBody,
} from "../api/autorivFasteners";

export function useAutorivFasteners(params: ListAutorivFastenersParams) {
  return useQuery<PaginatedAutorivFasteners>({
    queryKey: ["autoriv-fasteners", "list", params],
    queryFn: () => listAutorivFasteners(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useAutorivFastener(fastenerType: string | null) {
  return useQuery<AutorivFastener>({
    queryKey: ["autoriv-fasteners", "detail", fastenerType],
    queryFn: () => getAutorivFastener(fastenerType as string),
    enabled: !!fastenerType,
    staleTime: 30_000,
  });
}

export function useAutorivFastenerGroups() {
  return useQuery<{ groups: string[] }>({
    queryKey: ["autoriv-fasteners", "groups"],
    queryFn: () => listAutorivFastenerGroups(),
    staleTime: 5 * 60_000,
  });
}

export function useUpdateAutorivFastener() {
  const queryClient = useQueryClient();
  return useMutation<
    AutorivFastener,
    Error,
    { fastenerType: string; body: UpdateAutorivFastenerBody }
  >({
    mutationFn: ({ fastenerType, body }) =>
      updateAutorivFastener(fastenerType, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["autoriv-fasteners"] });
    },
  });
}

export function useCreateAutorivFastener() {
  const queryClient = useQueryClient();
  return useMutation<AutorivFastener, Error, CreateAutorivFastenerBody>({
    mutationFn: (body) => createAutorivFastener(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["autoriv-fasteners"] });
    },
  });
}

/** Soft-delete via DELETE /v1/autoriv-fasteners/:fastenerType. */
export function useDeleteAutorivFastener() {
  const queryClient = useQueryClient();
  return useMutation<{ success: true }, Error, string>({
    mutationFn: (fastenerType) => deleteAutorivFastener(fastenerType),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["autoriv-fasteners"] });
    },
  });
}
